import type { User, UserCredential } from 'firebase/auth';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { auth } from './config';

// Tenant ID mapping based on environment
const TENANT_IDS = {
  local: 'lead-management-dev-v5rch',
  dev: 'lead-management-dev-v5rch',
  qa: 'lead-management-qa-mshmn',
  prod: 'lead-management-yec4u',
} as const;

// Get current environment tenant ID
const getCurrentTenantId = (): string => {
  const env = import.meta.env.VITE_ENVIRONMENT as keyof typeof TENANT_IDS;
  return TENANT_IDS[env] || TENANT_IDS.local;
};

// Set tenant ID on auth instance
const setTenantId = () => {
  const tenantId = getCurrentTenantId();
  auth.tenantId = tenantId;
  if (import.meta.env.DEV) {
    console.debug(`Firebase Auth tenant set to: ${tenantId}`);
  }
};

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

export const authService = {
  // Sign in with Google SSO
  signInWithGoogle: async (): Promise<UserCredential> => {
    try {
      // Set tenant ID before signing in
      setTenantId();
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  },

  // Sign in with email and password
  signIn: async (email: string, password: string): Promise<UserCredential> => {
    return signInWithEmailAndPassword(auth, email, password);
  },

  // Sign up with email and password
  signUp: async (
    email: string,
    password: string,
    displayName?: string
  ): Promise<UserCredential> => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }

    return userCredential;
  },

  // Sign out
  signOut: async (): Promise<void> => {
    return signOut(auth);
  },

  // Reset password
  resetPassword: async (email: string): Promise<void> => {
    return sendPasswordResetEmail(auth, email);
  },

  // Get current user
  getCurrentUser: (): User | null => {
    return auth.currentUser;
  },

  // Update user profile
  updateUserProfile: async (updates: {
    displayName?: string;
    photoURL?: string;
  }): Promise<void> => {
    const user = auth.currentUser;
    if (user) {
      return updateProfile(user, updates);
    }
    throw new Error('No user is currently signed in');
  },

  // Auth state observer
  onAuthStateChange: (callback: (user: User | null) => void) => {
    // Set tenant ID when listening to auth state changes (for persisted sessions)
    setTenantId();
    return onAuthStateChanged(auth, callback);
  },
};

// Initialize tenant ID on module load
setTenantId();

export { auth };
